import { useEffect, useRef, useState } from 'react';
import { inrShort } from '../../utils/format';

const FALLBACK_W = 520;
const ROW_H = 34;
const PAD_L = 8;
const PAD_R = 64;
const TOP = 6;

/**
 * Horizontal ranked bars — label, track, fill, value.
 * `items`: [{ k, v, color? }]
 *
 * The SVG is drawn at the container's real pixel width so the bars fill the card.
 * (A fixed viewBox would letterbox: `preserveAspectRatio` centres it and leaves
 * dead space on both sides of a wide card.)
 */
export default function HBarChart({ items, color = 'var(--s1)', fmt = inrShort, unit = '' }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(FALLBACK_W);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!items.length) {
    return <div style={{ color: 'var(--muted)', fontSize: 13 }}>No data in range</div>;
  }

  const W = Math.max(320, width);
  const H = items.length * ROW_H + TOP + 4;
  const mx = Math.max(1, ...items.map((i) => i.v));

  // label column shrinks on narrow cards so the bar always has room
  const labelW = Math.min(150, Math.max(80, W * 0.3));
  const barX = labelW + 10;
  const barW = Math.max(20, W - barX - PAD_R);
  const maxChars = Math.max(8, Math.floor(labelW / 6.6));

  return (
    <div ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H}>
        {items.map((it, i) => {
          const y = TOP + i * ROW_H;
          const bw = Math.max(2, (barW * it.v) / mx);
          const c = it.color || color;
          const nm = it.k.length > maxChars ? it.k.slice(0, maxChars - 1) + '…' : it.k;
          return (
            <g key={`${it.k}-${i}`}>
              <text className="barname" x={PAD_L} y={y + ROW_H / 2} dominantBaseline="middle">{nm}</text>
              <rect x={barX} y={y + 6} width={barW} height={ROW_H - 14} rx={4} fill="var(--card-2)" />
              <rect
                x={barX} y={y + 6} width={bw} height={ROW_H - 14} rx={4} fill={c}
                data-t={`${it.k}||${fmt(it.v)}${unit}`}
              />
              <text className="barlabel" x={barX + bw + 8} y={y + ROW_H / 2} dominantBaseline="middle">
                {fmt(it.v)}{unit}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
